<script>
export default {
  name: 'AkSidebarV2',
  props: {
    dismissable: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: false,
    },
    blockScroll: {
      type: Boolean,
      default: true,
    },
    className: {
      type: String,
      default: '',
    },
    noHeader: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      visible: false,
      dragging: false,
      closingFromDrag: false,
      dragStartY: 0,
      dragOffset: 0,
      dragScrollEl: null,
    };
  },
  methods: {
    onDissmiss() {
      if (this.dismissable) this.close();
    },
    close() {
      this.visible = false;
      this.$emit('close');
      if (this.blockScroll) document.body.style.overflow = 'initial';
    },
    show() {
      if (this.blockScroll) document.body.style.overflow = 'hidden';
      this.visible = true;
    },
    onHandleDragStart(e) {
      if (window.innerWidth >= 768) return;
      this.dragging = true;
      this.dragStartY = e.touches[0].clientY;
      this.dragOffset = 0;
    },
    findScrollableAncestor(el) {
      const root = this.$refs.content;
      while (el && el !== root?.parentElement) {
        if (el.scrollHeight > el.clientHeight) return el;
        el = el.parentElement;
      }
      return root;
    },
    onContentDragStart(e) {
      if (!this.dismissable) return;
      if (window.innerWidth >= 768) return;
      // only arm dragging if content is scrolled to top; actual engagement
      // happens in onContentDragMove once we see an overscroll pull-down
      this.dragging = false;
      this.dragStartY = e.touches[0].clientY;
      this.dragOffset = 0;
      this.dragScrollEl = this.findScrollableAncestor(e.target);
    },
    onContentDragMove(e) {
      if (!this.dismissable) return;
      const delta = e.touches[0].clientY - this.dragStartY;

      if (!this.dragging) {
        if (this.dragScrollEl && this.dragScrollEl.scrollTop > 0) return;
        if (delta <= 0) return;
        this.dragging = true;
      }

      // prevent the page/content from also scrolling while we drag the sheet
      e.preventDefault();
      this.dragOffset = Math.max(0, delta);
    },
    onDragMove(e) {
      if (!this.dragging) return;
      const delta = e.touches[0].clientY - this.dragStartY;
      this.dragOffset = Math.max(0, delta);
    },
    onDragEnd() {
      if (!this.dragging) return;
      const panelHeight = this.$refs.panel?.offsetHeight || 0;
      const shouldClose = this.dragOffset > panelHeight * 0.25;

      if (shouldClose) {
        // animate onward from the current dragged position instead of
        // snapping back to 0 first, so the close doesn't "teleport" to top
        this.closingFromDrag = true;
        this.dragOffset = panelHeight;
        window.setTimeout(() => {
          // keep the inline transform pinned off-screen (still `dragging`)
          // while visible flips off, so the v-show leave transition has
          // nothing left to animate and doesn't replay from the top
          this.close();
          this.$nextTick(() => {
            this.dragging = false;
            this.closingFromDrag = false;
            this.dragOffset = 0;
          });
        }, 300);
      } else {
        this.dragging = false;
        this.dragOffset = 0;
      }
    },
  },
  computed: {
    hasTitle() {
      return typeof this.title === 'string';
    },
  },
};
</script>

<template>
  <teleport to="body">
    <transition name="fade-in">
      <div
        v-show="visible"
        class="fixed inset-0 z-[10000] w-screen h-dvh bg-gray-700/20"
        id="GaFilterMask"
        @click.exact="onDissmiss">
        <transition name="slide-in">
          <div
            v-show="visible"
            ref="panel"
            :class="className"
            :style="
              dragging
                ? {
                    transform: `translateY(${dragOffset}px)`,
                    transition: closingFromDrag ? 'transform 0.3s ease' : 'none',
                  }
                : null
            "
            class="absolute flex md:rounded-none rounded-t-3xl flex-col bottom-0 right-0 h-[80dvh] md:h-screen bg-white w-full md:w-1/3 md:max-w-[450px] md:min-w-[450px] shadow-lg shadow-grey-400/20"
            @click.stop>
            <!-- DRAG HANDLE (mobile only) -->
            <div
              v-if="noHeader"
              class="md:hidden flex justify-center pt-[7px] pb-[6px] shrink-0"
              @touchstart="onHandleDragStart"
              @touchmove="onDragMove"
              @touchend="onDragEnd">
              <div class="w-10 h-1 rounded-full bg-grey-200" />
            </div>
            <!--  HEADER  -->
            <div
              v-if="!noHeader"
              class="h-[86px] items-center px-8 py-6 flex justify-between sticky border-grey-100 border-b">
              <span v-if="hasTitle" class="text-[18px] font-semibold text-grey-900">{{ title }}</span>
              <button @click="close">
                <i class="ga-icon ga-supprimer text-3xl" />
              </button>
            </div>
            <div
              ref="content"
              class="flex-1 min-h-0 flex flex-col relative"
              @touchstart="onContentDragStart"
              @touchmove="onContentDragMove"
              @touchend="onDragEnd">
              <slot name="default" />
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity 0.1s ease-out;
}
.fade-in-enter-from,
.fade-in-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.3s ease;
}
.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateY(100%);
}

@media (min-width: 768px) {
  .slide-in-enter-from,
  .slide-in-leave-to {
    transform: translateX(100%);
  }
}
</style>
